import { clsx } from 'clsx'
import { ChevronRight } from 'lucide-react'
import { Fragment, useState } from 'react'
import { useI18n } from '@/i18n/i18n'
import { type Action, type ActionView, VERB, viewActions } from './actions'
import { MentionChip } from './SayText'

/** What a change touched: chips for things still on the board, else its hint in quotes. */
function Targets({ v, max }: { v: ActionView; max: number }) {
  const { t } = useI18n()
  if (!v.targets.length) return v.hint ? <span>「{v.hint}」</span> : null
  const shown = v.targets.slice(0, max)
  return (
    <>
      {shown.map((m, i) => (
        <MentionChip key={i} mention={m} fallback={v.hint ?? '…'} />
      ))}
      {v.targets.length > max && <span>{t('act.more', { n: v.targets.length - max })}</span>}
    </>
  )
}

/** "Created「夜與晨光」" — one change in a few words. */
function Phrase({ v, max }: { v: ActionView; max: number }) {
  const { t } = useI18n()
  return (
    <span className={clsx(v.failed && 'line-through decoration-muted/60')}>
      {/* Verbs leave room for what follows; with nothing after them, no trailing space. */}
      {v.targets.length || v.hint
        ? t(VERB[v.verb], { n: v.count })
        : t(VERB[v.verb], { n: v.count }).trimEnd()}
      <Targets v={v} max={max} />
    </span>
  )
}

/**
 * What the AI did to the board this turn, folded to one line of names
 * (DESIGN.md principle 5). Open, each change shows the intent the AI gave it.
 */
export function ActionsLine({ actions }: { actions: Action[] }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const views = viewActions(actions)
  if (!views.length) return null
  const folded = fold(views)
  const head = folded.slice(0, 2)
  return (
    <div className="text-[12px] leading-6 text-muted">
      <div
        className="flex cursor-pointer items-start gap-1"
        onClick={(e) => {
          if (!(e.target as HTMLElement).closest('[data-mention]')) setOpen((v) => !v)
        }}
      >
        <button
          type="button"
          aria-expanded={open}
          aria-label={t('act.toggle')}
          className="mt-1.5 flex-none hover:text-ink"
        >
          <ChevronRight size={12} className={clsx('transition-transform', open && 'rotate-90')} />
        </button>
        <span className="min-w-0">
          {head.map((v, i) => (
            <Fragment key={v.id}>
              {i > 0 && t('act.sep')}
              <Phrase v={v} max={2} />
            </Fragment>
          ))}
          {folded.length > head.length && t('act.moreChanges', { n: folded.length - head.length })}
        </span>
      </div>
      {open && (
        <ol className="mt-1 space-y-1.5 border-l border-line pl-3 ml-1.5">
          {views.map((v) => (
            <li key={v.id}>
              {v.intent && <div className="text-[12.5px] text-ink-2">{v.intent}</div>}
              <div>
                <Phrase v={v} max={8} />
                {v.failed && <span className="ml-1.5 text-danger">{t('act.failed')}</span>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

/**
 * The folded line names each thing once per kind of change: three edits to
 * one card read as one. Open, every step stays, with its own intent.
 */
function fold(views: ActionView[]): ActionView[] {
  const out: ActionView[] = []
  for (const v of views) {
    const same = !v.failed && !v.running && v.targets.length > 0
    const into = same && out.find((o) => o.verb === v.verb && !o.failed && !o.running)
    if (!into) {
      out.push(v)
      continue
    }
    const key = (m: (typeof v.targets)[number]) => JSON.stringify(m)
    const have = new Set(into.targets.map(key))
    const add = v.targets.filter((m) => !have.has(key(m)))
    const i = out.indexOf(into)
    out[i] = { ...into, targets: [...into.targets, ...add], count: into.count + add.length }
  }
  return out
}
